import { Component } from 'react'
import {
  clearClientCachesAndReload,
  recoverFromStaleBundle,
} from '../lib/stale-bundle'

/**
 * Top-level React error boundary.
 *
 * Without a boundary, ANY error thrown during render unmounts the whole
 * tree and leaves the user staring at a blank white `#root` — which is
 * exactly the "old account shows a white page after I deploy a change"
 * symptom. React surfaces a stale-build crash as the minified error
 * #130 ("element type is invalid … got: undefined"); that throw happens
 * during render, so the `window.onerror` listener in main.jsx can't
 * catch it — only a class error boundary can.
 *
 * Behaviour on catch:
 *   1. First crash → `recoverFromStaleBundle` does a one-time reload to
 *      fetch the freshly-deployed build. Most deploy-induced crashes
 *      vanish here and the user sees a ~1s flash, not a dead page.
 *   2. If we already reloaded inside the cooldown (the crash survived a
 *      fresh build, so it is a genuine bug or a dead network), we stop
 *      looping and render a readable fallback with manual recovery
 *      actions instead of a white screen.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, componentStack: '' }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // `info.componentStack` is the ONLY place React records WHICH
    // component rendered the bad element — a #130 ("element type is
    // undefined") never names it in the JS stack. We stash it so the
    // fallback can show it; with `esbuild.keepNames` on (vite.config.js)
    // those names stay readable in the production build instead of
    // mangled single letters.
    console.error(
      '[taskmagix] ErrorBoundary caught a render error:',
      error,
      info?.componentStack,
    )
    this.setState({ componentStack: info?.componentStack ?? '' })
    // Attempt the silent self-heal. If it returns false the cooldown is
    // active — we leave `hasError` true so the fallback below renders.
    recoverFromStaleBundle(`render-error: ${error?.message ?? error}`)
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
        />
      )
    }
    return this.props.children
  }
}

// Inline styles on purpose: if the crash was a failed CSS chunk, none of
// the app's Tailwind classes are available, so the fallback must stand on
// its own. Brand navy (`#02367B`) is hard-coded for the same reason.
function ErrorFallback({ error, componentStack }) {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        background: '#f5f7fa',
        color: '#0f172a',
      }}
    >
      <div
        style={{
          maxWidth: '30rem',
          width: '100%',
          textAlign: 'center',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '1rem',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          padding: '2.5rem 2rem',
        }}
      >
        {/* 404-style illustration from public/not-found.png. It's a static
         *  asset served independently of the JS/CSS bundle, so it still
         *  loads even when the crash was a failed chunk. `onError` hides it
         *  if the file is missing, degrading to a clean text-only card. */}
        <img
          src="/not-found.png"
          alt=""
          style={{
            display: 'block',
            width: '100%',
            maxWidth: '240px',
            height: 'auto',
            margin: '0 auto 1.5rem',
            userSelect: 'none',
          }}
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <h1
          style={{ fontSize: '1.375rem', fontWeight: 700, margin: '0 0 0.5rem' }}
        >
          Something went wrong.
        </h1>
        <p
          style={{
            fontSize: '0.95rem',
            color: '#475569',
            margin: '0 0 1.5rem',
            lineHeight: 1.5,
          }}
        >
          The app hit an unexpected error. This is almost always fixed by
          reloading to fetch the latest version.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={PRIMARY_BTN}
          >
            Reload Page
          </button>
          <button
            type="button"
            onClick={() => {
              void clearClientCachesAndReload()
            }}
            style={SECONDARY_BTN}
          >
            Clear Cache and Reload
          </button>
        </div>

        {/* Raw stack / component trace is a DEVELOPMENT-only aid. On a
         *  production build (`import.meta.env.DEV` is false) end users see
         *  only the friendly message + recovery buttons — exposing file
         *  paths, line numbers and component names to them is confusing and
         *  needlessly reveals the app's internals. The full error is still
         *  written to the browser console (componentDidCatch) for support. */}
        {import.meta.env.DEV && (error?.message || componentStack) ? (
          <details style={{ marginTop: '1.5rem', textAlign: 'left' }}>
            <summary
              style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#94a3b8' }}
            >
              Technical details
            </summary>
            <pre
              style={{
                marginTop: '0.5rem',
                maxHeight: '40vh',
                overflow: 'auto',
                padding: '0.75rem',
                background: '#0f172a',
                color: '#e2e8f0',
                borderRadius: '0.5rem',
                fontSize: '0.7rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {String(error?.message ?? error ?? '')}
              {componentStack}
            </pre>
          </details>
        ) : null}
      </div>
    </div>
  )
}

const PRIMARY_BTN = {
  cursor: 'pointer',
  border: '1px solid #02367B',
  background: '#02367B',
  color: '#ffffff',
  borderRadius: '0.5rem',
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  fontWeight: 600,
}

const SECONDARY_BTN = {
  cursor: 'pointer',
  border: '1px solid #cbd5e1',
  background: '#ffffff',
  color: '#02367B',
  borderRadius: '0.5rem',
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  fontWeight: 600,
}
